import cn from 'classnames';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  type ChatBodyProps,
  patientAgentMessages,
  type ThreadItem,
} from '../config';

import styles from './AgentChat.module.scss';
import { ApprovalCard } from './ApprovalCard';
import { ChatWelcome } from './ChatWelcome';
import { ConversationSkeleton } from './ConversationSkeleton';
import { DocumentCard } from './DocumentCard';
import { EventEntry } from './EventEntry';
import { MessageArticle } from './MessageArticle';

export const ChatBody: FC<ChatBodyProps> = ({
  variant,
  viewportRef,
  onScrollCapture,
  showWelcome,
  welcomeProps,
  documentAssistantCopy,
  isLoading,
  threadItems,
  streamdownKeySuffix,
  citationComponents,
  streamdownPlugins,
  errorText,
  onOpenDocument,
  showDiscard,
  onDiscardDocument,
  onApprovalDecision,
  discardingDocumentID,
  documentMode,
  documentID,
  isRunning,
  selectedDocumentText,
  dismissedProposals,
  onReplaceDocumentSelection,
  onInsertBelowDocumentSelection,
  onDismissProposal,
  onAttachmentPreviewChange,
  firstWorkEventID,
  workContextLine,
  className,
}) => {
  const intl = useIntl();

  // One pass over the timeline before rendering: for each index, the
  // previous timeline item (event or message) and the index where the next
  // message starts. Avoids rescanning the list inside the render map — the
  // naive version was O(n²) and ran on every streaming delta.
  const timelineMeta = useMemo(() => {
    const prevTimeline: (ThreadItem | undefined)[] = new Array(
      threadItems.length,
    );
    let lastTimeline: ThreadItem | undefined;
    for (let i = 0; i < threadItems.length; i += 1) {
      prevTimeline[i] = lastTimeline;
      const type = threadItems[i].type;
      if (type === 'event' || type === 'message') {
        lastTimeline = threadItems[i];
      }
    }
    // The group boundary is the next non-event item of ANY kind (message,
    // document, approval) — not just the next message: events that happen
    // after a document artifact must render after it, not be pulled into the
    // group that opened before it.
    const nextMessageAt: number[] = new Array(threadItems.length).fill(-1);
    let nextBoundary = -1;
    for (let i = threadItems.length - 1; i >= 0; i -= 1) {
      nextMessageAt[i] = nextBoundary;
      if (threadItems[i].type !== 'event') {
        nextBoundary = i;
      }
    }
    return { prevTimeline, nextMessageAt };
  }, [threadItems]);

  return (
    <section
      className={cn(
        styles.chatViewport,
        showWelcome && styles.chatViewportCentered,
        className,
      )}
      ref={viewportRef}
      onScrollCapture={onScrollCapture}
    >
      <div className={styles.chatViewportContent}>
        {isLoading ? <ConversationSkeleton /> : null}
        {showWelcome && !documentAssistantCopy ? (
          <ChatWelcome variant={variant} {...welcomeProps} />
        ) : null}
        {documentAssistantCopy ? (
          <div className={styles.documentAssistant}>
            <h4>{documentAssistantCopy.title}</h4>
            <p>{documentAssistantCopy.body}</p>
          </div>
        ) : null}
        {threadItems.map((item, index) => {
          if (item.type === 'message') {
            if (item.role === 'assistant' && !item.content) {
              return null;
            }
            return (
              <MessageArticle
                key={item.id}
                item={item}
                streamdownKey={`${item.id}:${streamdownKeySuffix}`}
                citationComponents={citationComponents}
                streamdownPlugins={streamdownPlugins}
                documentMode={
                  documentMode &&
                  (!item.selectionDocumentID ||
                    item.selectionDocumentID === documentID)
                }
                isRunning={isRunning}
                selectedDocumentText={selectedDocumentText}
                dismissedProposals={dismissedProposals}
                onReplaceDocumentSelection={onReplaceDocumentSelection}
                onInsertBelowDocumentSelection={onInsertBelowDocumentSelection}
                onDismissProposal={onDismissProposal}
                onAttachmentPreviewChange={onAttachmentPreviewChange}
              />
            );
          }
          if (item.type === 'document') {
            return (
              <DocumentCard
                key={item.id}
                item={item}
                showDiscard={showDiscard}
                discardingDocumentID={discardingDocumentID}
                onOpenDocument={onOpenDocument}
                onDiscardDocument={onDiscardDocument}
              />
            );
          }
          if (item.type === 'approval') {
            return (
              <ApprovalCard
                key={item.id}
                item={item}
                disabled={isRunning}
                onDecision={onApprovalDecision}
              />
            );
          }
          const previousTimelineItem = timelineMeta.prevTimeline[index];
          if (previousTimelineItem?.type === 'event') {
            return null;
          }
          const nextMessageIndex = timelineMeta.nextMessageAt[index];
          const groupedEvents = threadItems
            .slice(index, nextMessageIndex < 0 ? undefined : nextMessageIndex)
            .filter(
              (
                candidate,
              ): candidate is Extract<ThreadItem, { type: 'event' }> =>
                candidate.type === 'event',
            );
          return (
            <EventEntry
              key={item.id}
              items={groupedEvents}
              isFirstWorkEvent={item.id === firstWorkEventID}
              workContextLine={workContextLine}
            />
          );
        })}
        {errorText ? (
          <article className={cn(styles.message, styles.messageError)}>
            <p className={cn('p3', styles.messageRole)}>
              {intl.formatMessage(patientAgentMessages.roleError)}
            </p>
            <p className={cn('p2', styles.messageText)}>{errorText}</p>
          </article>
        ) : null}
      </div>
    </section>
  );
};
